import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import BrowseSectionTitle from "../../browse/components/BrowseSectionTitle";
import { getScreenRelease } from "../../../services/screenReleasesApi";
import ReleaseReadingOrderCard from "../components/ReleaseReadingOrderCard";
import {
  formatReleaseMeta,
  getAvailability,
  getReadingOrderLabel,
  getStatusChip,
  releaseQueryKey,
  releasesQueryOptions,
} from "../helpers/releases";

/**
 * A movie / show season: backdrop, poster, status, the linked reading
 * order(s) with start / continue, synopsis and TMDB / IMDb links.
 * Mirrors the mobile ScreenReleaseDetailScreen.
 */
const ScreenReleaseDetail = () => {
  const { releaseId } = useParams();
  const location = useLocation();
  const stateRelease = location.state?.release;

  const query = useQuery(
    releaseQueryKey(releaseId),
    () => getScreenRelease(releaseId),
    {
      ...releasesQueryOptions,
      // The list page hands over the release so the page paints right away.
      ...(stateRelease && String(stateRelease.id) === String(releaseId)
        ? { initialData: { data: stateRelease } }
        : {}),
    }
  );

  if (query.isLoading) {
    return <Spinner />;
  }

  const release = query.data?.data;
  if (query.isError || !release) {
    return (
      <Container maxW="container.lg">
        <Text mt="6">Unable to load this release.</Text>
      </Container>
    );
  }

  const availability = getAvailability(release);
  const statusChip = getStatusChip(release.status);
  const readingOrders = Array.isArray(release.readingOrders)
    ? release.readingOrders
    : [];

  const openLink = (site) =>
    trackEvent("release_external_link_click", { releaseId: release.id, site });

  return (
    <Container maxW="container.lg">
      <Breadcrumb mt="6">
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/browse">
            Browse
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/browse/cinematic-universes/list">
            Cinematic Universes
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink as={Link} to="#">
            {release.title}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg" overflow="hidden">
            {/* Backdrop + poster */}
            <Box position="relative" h="220px" bg="gray.800">
              {!!release.backdropUrl && (
                <Image
                  src={release.backdropUrl}
                  alt=""
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              )}
              <Box
                position="absolute"
                inset="0"
                bgGradient="linear(to-t, gray.700, transparent 60%)"
              />
              {!!availability && (
                <Badge
                  position="absolute"
                  top="3"
                  right="3"
                  colorScheme={availability.colorScheme}
                  variant={
                    availability.colorScheme === "red" ? "solid" : "subtle"
                  }
                  fontSize="sm"
                  px="2"
                  py="1"
                  borderRadius="md"
                >
                  {availability.label}
                </Badge>
              )}
            </Box>
            <Flex px="6" mt="-56px" alignItems="flex-end" position="relative">
              <Box
                w="96px"
                h="144px"
                flexShrink={0}
                borderRadius="md"
                overflow="hidden"
                bg="gray.800"
                borderWidth="1px"
                borderColor="gray.600"
              >
                {!!release.posterUrl && (
                  <Image
                    src={release.posterUrl}
                    alt=""
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                )}
              </Box>
              <Box pl="4" pb="2" minW="0">
                <Heading as="h2" size="lg">
                  {release.title}
                </Heading>
                <Text color="gray.400">{formatReleaseMeta(release)}</Text>
              </Box>
            </Flex>

            <Box px="6" pt="4" pb="6">
              {!!statusChip && (
                <Badge
                  colorScheme={statusChip.colorScheme}
                  variant="subtle"
                  borderRadius="md"
                  px="2"
                  py="1"
                >
                  {statusChip.label}
                </Badge>
              )}

              {!!release.overview && (
                <>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    mt="6"
                    mb="2"
                    textTransform="uppercase"
                  >
                    Synopsis
                  </Text>
                  <Text>{release.overview}</Text>
                </>
              )}

              {(!!release.tmdbUrl || !!release.imdbUrl) && (
                <Flex gap="3" mt="6">
                  {!!release.tmdbUrl && (
                    <Button
                      as="a"
                      href={release.tmdbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      flex="1"
                      rightIcon={<ExternalLinkIcon />}
                      onClick={() => openLink("tmdb")}
                    >
                      TMDB
                    </Button>
                  )}
                  {!!release.imdbUrl && (
                    <Button
                      as="a"
                      href={release.imdbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      flex="1"
                      rightIcon={<ExternalLinkIcon />}
                      onClick={() => openLink("imdb")}
                    >
                      IMDb
                    </Button>
                  )}
                </Flex>
              )}
            </Box>
          </Box>
        </Box>

        {/* Linked reading orders */}
        <Box>
          <BrowseSectionTitle mt="0">
            {getReadingOrderLabel(release, readingOrders.length)}
          </BrowseSectionTitle>
          {readingOrders.length ? (
            readingOrders.map((readingOrder) => (
              <ReleaseReadingOrderCard
                key={readingOrder.id}
                readingOrder={readingOrder}
                release={release}
              />
            ))
          ) : (
            <Text fontSize="sm" color="gray.400">
              No reading order linked to this release yet.
            </Text>
          )}
        </Box>
      </SimpleGrid>
    </Container>
  );
};

export default ScreenReleaseDetail;
